(() => {
  const PARAM_NAMES = ['pin', 'first', 'priority_products', 'promote'];
  const GRID_SELECTOR = '#product-grid, .product-grid';
  const ITEM_SELECTOR = 'li.grid__item, .grid__item, [data-product-handle], [data-product-id]';
  let hasApplied = false;
  let lastSignature = '';
  let pendingTimer = null;
  let isApplying = false;
  let revealTimer = null;

  function normalizeToken(value) {
    try {
      return decodeURIComponent(String(value || ''))
        .trim()
        .replace(/^https?:\/\/[^/]+/i, '')
        .replace(/^\/products\//i, '')
        .replace(/^products\//i, '')
        .split(/[?#]/)[0]
        .replace(/^gid:\/\/shopify\/Product\//i, '')
        .toLowerCase();
    } catch (_error) {
      return String(value || '').trim().toLowerCase();
    }
  }

  function splitPinnedValue(value) {
    return String(value || '')
      .split(/[,+|;\s]+/)
      .map(normalizeToken)
      .filter(Boolean);
  }

  function getPinnedProductsFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const tokens = [];

    PARAM_NAMES.forEach((name) => {
      params.getAll(name).forEach((value) => tokens.push(...splitPinnedValue(value)));
      params.getAll(`${name}[]`).forEach((value) => tokens.push(...splitPinnedValue(value)));
    });

    const seen = new Set();
    return tokens.filter((token) => {
      if (!token || seen.has(token)) return false;
      seen.add(token);
      return true;
    });
  }

  function getHandleFromHref(href) {
    const match = String(href || '').match(/\/products\/([^/?#]+)/i);
    return match ? normalizeToken(match[1]) : '';
  }

  function getItemHandles(item) {
    const handles = new Set();
    [item.dataset.productHandle, item.getAttribute('data-product-handle'), item.dataset.productUrl, item.getAttribute('data-product-url')]
      .forEach((value) => {
        const token = normalizeToken(value);
        if (token) handles.add(token);
      });

    item.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      const handle = getHandleFromHref(link.getAttribute('href'));
      if (handle) handles.add(handle);
    });

    return Array.from(handles);
  }

  function matchesPinnedProduct(item, token) {
    const id = normalizeToken(item.dataset.productId || item.getAttribute('data-product-id'));
    if (id && token === id) return true;
    return getItemHandles(item).some((handle) => token === handle || handle.endsWith(`/${token}`));
  }

  function findGrid() {
    return document.querySelector(GRID_SELECTOR);
  }

  function findGridItems(grid) {
    return Array.from(grid.querySelectorAll(ITEM_SELECTOR)).filter((item) => {
      const gridItem = item.matches('li.grid__item, .grid__item') ? item : item.closest('li.grid__item, .grid__item');
      return gridItem && gridItem.parentElement === grid;
    }).map((item) => item.matches('li.grid__item, .grid__item') ? item : item.closest('li.grid__item, .grid__item'))
      .filter((item, index, arr) => item && arr.indexOf(item) === index);
  }

  function updateAnimationOrder(grid) {
    Array.from(grid.children).forEach((item, index) => {
      if (item.style) item.style.setProperty('--animation-order', index + 1);
    });
  }

  function markPinPending() {
    if (getPinnedProductsFromUrl().length) {
      document.documentElement.classList.add('pin-products-pending');
    } else {
      document.documentElement.classList.remove('pin-products-pending');
    }
  }

  function revealGrid(grid) {
    window.clearTimeout(revealTimer);
    document.documentElement.classList.remove('pin-products-pending');
    if (grid) grid.classList.remove('pin-products-grid-pending');
  }

  function revealGridSoon(grid) {
    window.clearTimeout(revealTimer);
    revealTimer = window.setTimeout(() => revealGrid(grid), 700);
  }

  function scrollToFirstPinned(selected) {
    const params = new URLSearchParams(window.location.search);
    const shouldScroll = params.get('pin_scroll') !== '0' && params.get('scroll') !== '0';
    if (hasApplied || !shouldScroll || selected.length === 0) return;
    const headerOffset = document.querySelector('sticky-header, .shopify-section-header-sticky, header')?.offsetHeight || 0;
    const top = selected[0].getBoundingClientRect().top + window.scrollY - headerOffset - 16;
    window.setTimeout(() => window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' }), 120);
  }

  function applyPinnedProducts() {
    if (isApplying) return false;
    const pins = getPinnedProductsFromUrl();
    const grid = findGrid();
    if (!grid || pins.length === 0) {
      revealGrid(grid);
      return false;
    }

    grid.classList.add('pin-products-grid-pending');
    revealGridSoon(grid);

    const items = findGridItems(grid);
    const signature = pins.join(',') + '|' + items.map((item) => item.dataset.productHandle || getItemHandles(item)[0] || '').join(',');
    if (hasApplied && signature === lastSignature) {
      revealGrid(grid);
      return true;
    }

    const selected = [];
    const selectedSet = new Set();
    pins.forEach((token) => {
      const item = items.find((candidate) => !selectedSet.has(candidate) && matchesPinnedProduct(candidate, token));
      if (item) {
        selected.push(item);
        selectedSet.add(item);
      }
    });

    grid.dataset.pinnedProductsRequested = pins.join(',');
    if (selected.length === 0) {
      grid.dataset.pinnedProductsApplied = 'not-found';
      revealGrid(grid);
      return false;
    }

    isApplying = true;

    const fragment = document.createDocumentFragment();
    selected.forEach((item) => fragment.appendChild(item));
    grid.insertBefore(fragment, grid.firstElementChild);

    updateAnimationOrder(grid);
    selected.forEach((item) => {
      item.setAttribute('data-pinned-product', 'true');
      item.classList.add('js-pinned-product');
      item.style.removeProperty('display');
      item.style.removeProperty('visibility');
      item.style.removeProperty('opacity');
      item.querySelectorAll('img[loading="lazy"]').forEach((img) => img.setAttribute('loading', 'eager'));
    });
    grid.dataset.pinnedProductsApplied = selected.map((item) => item.dataset.productHandle || getItemHandles(item)[0] || item.dataset.productId || '').join(',');
    lastSignature = pins.join(',') + '|' + Array.from(grid.children).map((item) => item.dataset.productHandle || getItemHandles(item)[0] || '').join(',');
    scrollToFirstPinned(selected);
    hasApplied = true;
    revealGrid(grid);
    window.setTimeout(() => { isApplying = false; }, 0);
    return true;
  }

  function scheduleApply(delay = 0) {
    window.clearTimeout(pendingTimer);
    pendingTimer = window.setTimeout(() => {
      window.requestAnimationFrame(applyPinnedProducts);
    }, delay);
  }

  markPinPending();
  scheduleApply(0);
  document.addEventListener('DOMContentLoaded', () => scheduleApply(0));
  window.addEventListener('load', () => scheduleApply(0));
  window.addEventListener('popstate', () => {
    hasApplied = false;
    lastSignature = '';
    markPinPending();
    scheduleApply(0);
  });
  document.addEventListener('shopify:section:load', () => {
    hasApplied = false;
    lastSignature = '';
    markPinPending();
    scheduleApply(0);
  });

  const productGridContainer = document.querySelector('#ProductGridContainer') || document.body;
  const observer = new MutationObserver((mutations) => {
    if (isApplying) return;
    if (mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => node.nodeType === 1 && (node.matches?.(GRID_SELECTOR) || node.querySelector?.(GRID_SELECTOR))))) {
      hasApplied = false;
      lastSignature = '';
      markPinPending();
      scheduleApply(0);
    }
  });
  observer.observe(productGridContainer, { childList: true, subtree: true });
})();
